import { Box, Button, Divider, Menu, MenuItem, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { resources } from '../../core/services/resources';
import { NewLineOfBizDialog } from './NewLineOfBizDialog';
import type { LineOfBiz } from '../../core/types/data-models';

interface LineOfBizSwitcherProps {
  fullWidth?: boolean;
  /** Currently-selected LOB id (controlled). `null` for "no selection". */
  value: number | null;
  onChange: (id: number | null) => void;
}

/**
 * One row in the flattened tree the menu renders. `depth` drives
 * left-padding so a child appears indented under its parent.
 */
interface TreeRow {
  lob: LineOfBiz;
  depth: number;
}

/**
 * Depth-first flatten of the LOB list using `parentId` to build the
 * tree shape. Ordering inside each level follows the input array, which
 * is sorted by name by the caller — so siblings appear alphabetically
 * and the whole menu reads like a folder tree.
 */
function flattenTree(items: LineOfBiz[]): TreeRow[] {
  const byParent = new Map<number | null, LineOfBiz[]>();
  for (const i of items) {
    const k = i.parentId ?? null;
    const list = byParent.get(k) ?? [];
    list.push(i);
    byParent.set(k, list);
  }
  const out: TreeRow[] = [];
  const walk = (parentId: number | null, depth: number) => {
    for (const child of byParent.get(parentId) ?? []) {
      out.push({ lob: child, depth });
      walk(child.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

/**
 * Sidebar selector for the active LineOfBiz. Modelled after
 * `TenantSwitcher` — a small dropdown button that opens a menu with the
 * full tree of LOBs and a "+ New line of business" item at the bottom.
 *
 * Selection is *controlled* (the parent owns the `value` state) because
 * the LOB scope isn't carried by the JWT yet — there's no equivalent of
 * `/auth/switch-tenant` to call. When LOB scope lands server-side,
 * change `onChange` to call that endpoint instead of mutating local
 * state and the rest of this component stays the same.
 */
export function LineOfBizSwitcher({ fullWidth, value, onChange }: LineOfBizSwitcherProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const lobsQuery = useQuery({
    // Same cache key the rest of the app uses for LOB lookups, so this
    // doesn't trigger a second fetch when other pages already loaded it.
    queryKey: ['lines-of-business', 'select'],
    queryFn: () => resources.linesOfBusiness.list({ pageSize: 200, sort: 'name' }),
  });
  const lobs = lobsQuery.data?.data ?? [];

  const tree = useMemo(() => flattenTree(lobs), [lobs]);

  // Auto-select the first LOB whenever the catalog is loaded and nothing
  // is currently selected. Covers two cases:
  //   1. Initial sidebar render before the user has clicked anything.
  //   2. Tenant switch — `LineOfBizContext` resets `value` to null on
  //      tenant change, so this effect fires again with the new tenant's
  //      LOBs to land on a usable default.
  // We pick the first entry in the *flattened tree* so the visible order
  // matches what the menu shows (alphabetical, parents before children).
  useEffect(() => {
    if (value != null) return;
    if (lobsQuery.isLoading) return;
    const first = tree[0]?.lob;
    if (first) onChange(first.id);
  }, [value, tree, lobsQuery.isLoading, onChange]);

  // Look up the currently-selected row so the trigger button can show
  // its name. Walks `lobs` once; cheap for the typical sub-200-row
  // catalog.
  const activeName = useMemo(() => {
    if (value == null) return 'Select line of business';
    const match = lobs.find((l) => l.id === value);
    return match?.name ?? `Line of business #${value}`;
  }, [value, lobs]);

  const open = Boolean(anchor);

  const handleSelect = (lob: LineOfBiz) => {
    setAnchor(null);
    if (lob.id !== value) onChange(lob.id);
  };

  return (
    <>
      <Button
        size="small"
        fullWidth={fullWidth}
        onClick={(e) => setAnchor(e.currentTarget)}
        endIcon={<ExpandMoreIcon />}
        variant="outlined"
        sx={{
          textTransform: 'none',
          minWidth: 200,
          justifyContent: 'space-between',
          backgroundColor: 'background.paper',
        }}
      >
        <Typography variant="body2" fontWeight={600} noWrap>
          {activeName}
        </Typography>
      </Button>

      <Menu
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 280, maxHeight: 420 } } }}
      >
        {lobsQuery.isLoading && (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              Loading lines of business…
            </Typography>
          </MenuItem>
        )}
        {!lobsQuery.isLoading && tree.length === 0 && (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              No lines of business yet
            </Typography>
          </MenuItem>
        )}
        {tree.map(({ lob, depth }) => {
          const isActive = lob.id === value;
          return (
            <MenuItem
              key={lob.id}
              selected={isActive}
              onClick={() => handleSelect(lob)}
              // Indent per depth level so the tree shape reads at a
              // glance. The base padding-left matches MUI's default, and
              // each level adds 2 spacing units.
              sx={{ pl: 2 + depth * 2 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Typography
                  variant="body2"
                  fontWeight={isActive ? 600 : 400}
                  sx={{ flexGrow: 1 }}
                >
                  {lob.name}
                </Typography>
                {isActive && <CheckIcon fontSize="small" color="primary" />}
              </Box>
            </MenuItem>
          );
        })}

        <Divider />
        <MenuItem
          onClick={() => {
            setAnchor(null);
            setCreateOpen(true);
          }}
        >
          <AddIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="body2" color="primary" fontWeight={600}>
            New line of business
          </Typography>
        </MenuItem>
      </Menu>

      <NewLineOfBizDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(lob) => {
          // Jump straight to the newly-created LOB so the user sees
          // their selection take effect without a second click.
          onChange(lob.id);
        }}
      />
    </>
  );
}
